import { useCallback, useEffect, useMemo, useRef } from 'react';

import { $convertFromMarkdownString, $convertToMarkdownString } from '@lexical/markdown';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { LexicalComposer, type InitialConfigType } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import type { Transformer } from '@lexical/markdown';
import { LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import type { EditorState, Klass, LexicalNode } from 'lexical';

import { cn } from '@/lib/utils';

import { lexicalMarkdownTheme } from './lexicalMarkdownTheme';
import { LEXICAL_MARKDOWN_TRANSFORMERS } from './markdownTransformers';
import { ToolbarPlugin } from './plugins/ToolbarPlugin';

type WritableRef<T> = {
  current: T;
};

export type LexicalMarkdownEditorProps = {
  /** Controlled markdown value (imported into Lexical). */
  value: string;
  /** Called with markdown exported from Lexical. */
  onChange(nextMarkdown: string): void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Whether the editor should autofocus on mount. */
  autoFocus?: boolean;
  /** Custom namespace for Lexical editor instance. */
  namespace?: string;
  toolbar?: 'none' | 'basic';
  /** Additional Lexical node classes to register beyond the default set
   *  (headings, lists, quotes, links). Consumers that need tables,
   *  images, etc. pass the relevant node classes here. */
  extraNodes?: ReadonlyArray<Klass<LexicalNode>>;
  /** Additional markdown transformers to apply on top of the default
   *  set. Mirrors ``extraNodes`` and must be passed together with any
   *  nodes the transformers depend on. */
  extraTransformers?: ReadonlyArray<Transformer>;
};

function onError(error: Error) {
  console.error(error);
}

function SetEditablePlugin({ editable }: { editable: boolean }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    editor.setEditable(editable);
  }, [editor, editable]);

  return null;
}

function SyncMarkdownFromPropsPlugin(props: {
  value: string;
  transformers: typeof LEXICAL_MARKDOWN_TRANSFORMERS;
  suppressOnChangeRef: WritableRef<number>;
  lastEmittedRef: WritableRef<string | null>;
}) {
  const { value, transformers, suppressOnChangeRef, lastEmittedRef } = props;
  const [editor] = useLexicalComposerContext();

  const lastAppliedRef = useRef<string | null>(null);

  useEffect(() => {
    // Avoid re-importing the same markdown repeatedly.
    if (lastAppliedRef.current === value) return;

    // If the parent is just reflecting back what we emitted from the editor,
    if (lastEmittedRef.current === value) {
      lastAppliedRef.current = value;
      return;
    }

    // Suppress the next OnChange export that will result from this update.
    suppressOnChangeRef.current = suppressOnChangeRef.current + 1;
    lastAppliedRef.current = value;
    lastEmittedRef.current = null;

    editor.update(() => {
      $convertFromMarkdownString(value, transformers);
    });
  }, [editor, lastEmittedRef, suppressOnChangeRef, transformers, value]);

  return null;
}

export function LexicalMarkdownEditor({
  value,
  onChange,
  placeholder = 'Write…',
  disabled = false,
  className,
  autoFocus = false,
  namespace = 'LotushealthMarkdownEditor',
  toolbar = 'basic',
  extraNodes,
  extraTransformers,
}: LexicalMarkdownEditorProps) {
  const transformers = useMemo<Transformer[]>(
    () => [...LEXICAL_MARKDOWN_TRANSFORMERS, ...(extraTransformers ?? [])],
    [extraTransformers]
  );
  const suppressOnChangeRef = useRef(0);
  const lastEmittedRef = useRef<string | null>(null);

  const initialConfig = useMemo<InitialConfigType>(
    () => ({
      namespace,
      theme: lexicalMarkdownTheme,
      onError,
      nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode, ...(extraNodes ?? [])],
      editorState: () => $convertFromMarkdownString(value, transformers),
      editable: !disabled,
    }),
    [disabled, extraNodes, namespace, transformers, value]
  );

  const handleChange = useCallback(
    (editorState: EditorState) => {
      if (suppressOnChangeRef.current > 0) {
        suppressOnChangeRef.current = suppressOnChangeRef.current - 1;
        return;
      }

      editorState.read(() => {
        const markdown = $convertToMarkdownString(transformers);
        if (markdown === value) return;
        lastEmittedRef.current = markdown;
        onChange(markdown);
      });
    },
    [onChange, transformers, value]
  );

  return (
    <LexicalComposer initialConfig={initialConfig}>
      {toolbar === 'basic' ? <ToolbarPlugin disabled={disabled} /> : null}

      <div className={cn('lexical-editor-shell', disabled && 'lexical-editor-disabled', className)}>
        <RichTextPlugin
          contentEditable={
            <ContentEditable
              className="lexical-content-editable"
              aria-placeholder={placeholder}
              aria-disabled={disabled}
              placeholder={() => null}
            />
          }
          placeholder={<div className="lexical-placeholder">{placeholder}</div>}
          ErrorBoundary={LexicalErrorBoundary}
        />
      </div>

      <HistoryPlugin />
      <MarkdownShortcutPlugin transformers={transformers} />
      <OnChangePlugin onChange={handleChange} />
      <SetEditablePlugin editable={!disabled} />
      <SyncMarkdownFromPropsPlugin
        value={value}
        transformers={transformers}
        suppressOnChangeRef={suppressOnChangeRef}
        lastEmittedRef={lastEmittedRef}
      />
      {autoFocus ? <AutoFocusPlugin /> : null}
    </LexicalComposer>
  );
}
